import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {fetchRest} from '../helpers/fetchRest';
import utils from '../helpers/utils';

// Query key for FCRA consent state
export const FCRA_CONSENT_QUERY_KEY = ['fcraConsent'];

// Define the interface for our FCRA consent state
export interface FcraConsentState {
  isModalShown: boolean;
  isAccepted: boolean;
  error: unknown;
  isSuccessModalShown: boolean;
}

// Define the default state
const defaultFcraState: FcraConsentState = {
  isModalShown: false,
  isAccepted: false,
  error: {},
  isSuccessModalShown: false,
};

/**
 * Custom hook for FCRA consent functionality
 * Uses React Query for state management and API interactions
 */
export function useFcraConsent() {
  const queryClient = useQueryClient();

  // Use useQuery to make the state reactive - this ensures components re-render when state changes
  const {data: fcraState = defaultFcraState} = useQuery({
    queryKey: FCRA_CONSENT_QUERY_KEY,
    queryFn: () => {
      // Return the current state from cache or default
      return queryClient.getQueryData<FcraConsentState>(FCRA_CONSENT_QUERY_KEY) || defaultFcraState;
    },
    staleTime: Infinity,
    gcTime: Infinity,
    initialData: defaultFcraState,
  });

  // Mutation for accepting FCRA consent
  const mutation = useMutation({
    mutationKey: ['fcraConsent'],
    mutationFn: () => {
      const url = utils.getFcraConsentPath();
      const config = {
        method: 'POST' as const,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': utils.getCookieValue(document.cookie, 'XSRF-TOKEN'),
        },
        credentials: 'include' as RequestCredentials,
      };
      return fetchRest(url, config);
    },
    onSuccess: () => {
      // Update the React Query cache
      queryClient.setQueryData(FCRA_CONSENT_QUERY_KEY, {
        ...fcraState,
        isAccepted: true,
        isModalShown: true,
      });
    },
    onError: (error: unknown) => {
      // Update the React Query cache with error
      queryClient.setQueryData(FCRA_CONSENT_QUERY_KEY, {
        ...fcraState,
        error,
        isModalShown: true,
      });
    },
  });

  // Method to handle setting the success modal state
  const setSuccessModalState = () => {
    queryClient.setQueryData(FCRA_CONSENT_QUERY_KEY, {
      ...fcraState,
      isSuccessModalShown: true,
    });
  };

  return {
    // Expose mutation state
    isLoading: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error,

    // Expose FCRA state from React Query cache
    fcraState,

    // Methods
    acceptFcra: mutation.mutate,
    setSuccessModalState,
  };
}
